import { useState } from "react";
import { NavLink } from "react-router-dom";
import { AlertCircle, CalendarClock, RefreshCw } from "lucide-react";
import { BarList, CellStack, denseTable, EmptyValue, MetricGrid, Section, SegmentedControl, type SegmentedOption, StateBlock } from "@/components/layout-2/ds";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { formatParis, humanizeToken, utcTooltip } from "@/utils/dialer-datetime";
import { formatNumber, formatPercent } from "@/utils/format-fr";

type Period = "1h" | "24h" | "48h";
type WrapupRow = { name: string; status: string | null; count: number; percentage: number };
type WrapupFilter = { wrapup: string; wrapupStatus: string };

const ALL_WRAPUPS = "__all__";
const ALL_CALLER_NUMBERS = "__all__";
const NO_WRAPUP = "Unknown / No wrapup";
const NO_WRAPUP_FILTER = "__no_wrapup__";
const NO_WRAPUP_STATUS_FILTER = "__no_wrapup_status__";

function wrapupFilter(row: WrapupRow): WrapupFilter {
    return {
        wrapup: row.name === NO_WRAPUP ? NO_WRAPUP_FILTER : row.name,
        wrapupStatus: row.status ?? NO_WRAPUP_STATUS_FILTER
    };
}

function wrapupFilterValue(filter: WrapupFilter): string {
    return JSON.stringify([filter.wrapup, filter.wrapupStatus]);
}

function parseWrapupFilter(value: string): WrapupFilter {
    const [wrapup, wrapupStatus] = JSON.parse(value) as [string, string];
    return { wrapup, wrapupStatus };
}

function wrapupLabel(row: WrapupRow): string {
    return row.status ? `${row.name} · ${humanizeToken(row.status)}` : row.name;
}

type CallerNumberRow = {
    phone_number: string | null;
    label: string | null;
    display: string;
    attempts: number;
    recorded_calls: number;
    picked_up: number;
    pickup_rate: number | null;
    average_talk_seconds: number | null;
    average_duration_seconds: number | null;
    underperforming: boolean;
};

export type CampaignAnalyticsContent = {
    period: Period;
    available_periods: Period[];
    caller_number: string | null;
    caller_numbers: string[];
    called: Record<Period, { unique_cards: number; attempts: number }>;
    total_calls: number;
    callbacks_scheduled: number;
    reachability: {
        attempts: number;
        conversations: number;
        picked_up: number;
        rate: number | null;
    };
    wrapups: WrapupRow[];
    duration: {
        kind: "total_call";
        unit: "seconds";
        calls_with_duration: number;
        calls_without_duration: number;
        total_seconds: number;
        average_seconds: number | null;
        median_seconds: number | null;
    };
    caller_number_stats: {
        campaign_pickup_rate: number | null;
        min_recorded_to_flag: number;
        pickup_gap_pp: number;
        results: CallerNumberRow[];
    };
    recent_calls: {
        count: number;
        limit: number;
        offset: number;
        next_offset: number | null;
        previous_offset: number | null;
        results: Array<{
            event_id: number;
            provider_call_id: string | null;
            provider_call_url: string;
            recorded_at: string;
            started_at: string | null;
            card: { id: number | null; label: string; url: string | null };
            node_id: string | null;
            wrapup: string;
            wrapup_status: string | null;
            provider_result: string | null;
            caller_number: string | null;
            duration_seconds: number | null;
            callback_scheduled: boolean;
        }>;
    };
};

function formatDuration(value: number | null): string {
    if (value === null) return "—";
    const rounded = Math.round(value);
    const hours = Math.floor(rounded / 3600);
    const minutes = Math.floor((rounded % 3600) / 60);
    const seconds = rounded % 60;
    return [hours ? `${hours}h` : "", minutes || hours ? `${minutes}m` : "", `${seconds}s`].filter(Boolean).join(" ");
}

const PERIOD_OPTIONS: SegmentedOption<Period>[] = [
    { value: "1h", label: "Last hour" },
    { value: "24h", label: "Last 24 hours" },
    { value: "48h", label: "Last 48 hours" }
];

export function CampaignAnalyticsPanel({ campaignId, initial }: { campaignId: string | number; initial: CampaignAnalyticsContent }) {
    const [content, setContent] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedCallerNumber, setSelectedCallerNumber] = useState<string | null>(initial.caller_number);
    const [selectedWrapup, setSelectedWrapup] = useState<WrapupFilter | null>(null);

    const load = async (
        options: {
            period?: Period;
            offset?: number;
            callerNumber?: string | null;
            wrapup?: WrapupFilter | null;
        } = {}
    ) => {
        const period = options.period ?? content.period;
        const offset = options.offset ?? 0;
        const callerNumber = options.callerNumber === undefined ? selectedCallerNumber : options.callerNumber;
        const wrapup = options.wrapup === undefined ? selectedWrapup : options.wrapup;
        setLoading(true);
        setError(null);
        try {
            const query = new URLSearchParams({ period, offset: String(offset), limit: String(content.recent_calls.limit) });
            if (callerNumber) query.set("caller_number", callerNumber);
            if (wrapup) {
                query.set("wrapup", wrapup.wrapup);
                query.set("wrapup_status", wrapup.wrapupStatus);
            }
            const next = (await DataService.client.url(`/crm/objects/dialer-campaigns/${campaignId}/analytics/?${query}`).get().json()) as CampaignAnalyticsContent;
            setContent(next);
            setSelectedCallerNumber(callerNumber);
            setSelectedWrapup(wrapup);
        } catch {
            setError("Campaign analytics could not be loaded.");
        } finally {
            setLoading(false);
        }
    };

    const isEmpty = content.total_calls === 0 && Object.values(content.called).every(window => window.attempts === 0);

    const selectedWrapupKey = selectedWrapup ? wrapupFilterValue(selectedWrapup) : null;

    return (
        <div className="flex flex-col gap-4" aria-busy={loading}>
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Analytics unavailable</AlertTitle>
                    <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                        <span>{error}</span>
                        <Button size="sm" variant="outline" onClick={() => load()}>
                            <RefreshCw className="mr-1 h-4 w-4" /> Retry
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <MetricGrid
                columns={6}
                metrics={[
                    ...(["1h", "24h", "48h"] as Period[]).map(period => ({
                        label: `Unique cards called · ${period}`,
                        value: formatNumber(content.called[period].unique_cards),
                        hint: `${formatNumber(content.called[period].attempts)} provider-accepted attempt${content.called[period].attempts === 1 ? "" : "s"}`
                    })),
                    { label: `Callbacks scheduled · ${content.period}`, value: formatNumber(content.callbacks_scheduled), hint: "Backed by a callback event" },
                    { label: `Recorded calls · ${content.period}`, value: formatNumber(content.total_calls), hint: "Deduplicated provider reports" },
                    {
                        label: `Reachability · ${content.period}`,
                        value: formatPercent(content.reachability.rate),
                        hint: `${formatNumber(content.reachability.picked_up)} picked up / ${formatNumber(content.reachability.attempts)} attempts · ${formatNumber(content.reachability.conversations)} connected`
                    }
                ]}
            />

            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-gray-500">Call outcomes · the caller number and the period apply to every block below. Deduplicated recorded calls, times in Europe/Paris.</p>
                <div className="flex flex-wrap gap-2">
                    <Select
                        value={selectedCallerNumber ?? ALL_CALLER_NUMBERS}
                        onValueChange={value => load({ callerNumber: value === ALL_CALLER_NUMBERS ? null : value, offset: 0, wrapup: null })}
                        disabled={loading}
                    >
                        <SelectTrigger className="h-8 w-52 bg-white" aria-label="Filter analytics by caller number">
                            <SelectValue placeholder="All caller numbers" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL_CALLER_NUMBERS}>All caller numbers</SelectItem>
                            {content.caller_numbers.map(number => (
                                <SelectItem key={number} value={number}>
                                    {number}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <SegmentedControl
                        ariaLabel="Analytics period"
                        value={content.period}
                        options={PERIOD_OPTIONS}
                        onChange={period => {
                            if (!loading && period !== content.period) load({ period, offset: 0, wrapup: null });
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <StateBlock kind="loading" />
            ) : isEmpty ? (
                <StateBlock message="No calls yet: this campaign has not placed a provider-accepted call. Analytics will appear here after its first attempt." />
            ) : (
                <div>
                    <div className="grid gap-x-4 lg:grid-cols-2">
                        <Section icon="ListChecks" title="Wrapups">
                            {content.wrapups.length === 0 ? (
                                <StateBlock message="No recorded wrapup in this period." className="py-4" />
                            ) : (
                                <BarList
                                    scale="total"
                                    selected={selectedWrapupKey}
                                    rows={content.wrapups.map(row => ({
                                        key: wrapupFilterValue(wrapupFilter(row)),
                                        name: row.name,
                                        count: row.count,
                                        percentage: row.percentage,
                                        hint: row.status ? humanizeToken(row.status) : undefined,
                                        ariaLabel: `Filter recent calls by ${wrapupLabel(row)}`
                                    }))}
                                    valueLabel={row => `${formatNumber(row.count)} · ${formatPercent(row.percentage)}`}
                                    onSelect={row => {
                                        const wrapup = content.wrapups.find(candidate => wrapupFilterValue(wrapupFilter(candidate)) === row.key);
                                        if (wrapup) load({ offset: 0, wrapup: wrapupFilter(wrapup) });
                                    }}
                                />
                            )}
                        </Section>

                        <Section icon="Clock" title="Total call duration" className="lg:mt-0">
                            <MetricGrid
                                columns={2}
                                metrics={[
                                    { label: "Total", value: formatDuration(content.duration.total_seconds) },
                                    { label: "Average", value: formatDuration(content.duration.average_seconds) },
                                    { label: "Median", value: formatDuration(content.duration.median_seconds) },
                                    {
                                        label: "Calls measured",
                                        value: formatNumber(content.duration.calls_with_duration),
                                        hint: `${formatNumber(content.duration.calls_without_duration)} without duration`
                                    }
                                ]}
                            />
                            <p className="text-xs text-gray-500">Provider total call duration, not conversation-only time.</p>
                        </Section>
                    </div>

                    <Section icon="Phone" title="Caller numbers">
                        <p className="text-xs text-gray-500">
                            Pickup is talk time above zero. A number is flagged when pickup trails this campaign by at least {formatNumber(content.caller_number_stats.pickup_gap_pp, 1)} pp with{" "}
                            {content.caller_number_stats.min_recorded_to_flag}+ recorded calls. Campaign pickup: {formatPercent(content.caller_number_stats.campaign_pickup_rate)}.
                        </p>
                        {content.caller_number_stats.results.length === 0 ? (
                            <StateBlock message="No caller id was journalled on attempts in this period." />
                        ) : (
                            <Table>
                                <TableHeader className={denseTable.header}>
                                    <TableRow className={denseTable.headerRow}>
                                        <TableHead className={denseTable.head}>Number</TableHead>
                                        <TableHead className={cn(denseTable.head, "text-right")}>Attempts</TableHead>
                                        <TableHead className={cn(denseTable.head, "text-right")}>Recorded</TableHead>
                                        <TableHead className={cn(denseTable.head, "text-right")}>Pickup</TableHead>
                                        <TableHead className={cn(denseTable.head, "text-right")}>Avg talk</TableHead>
                                        <TableHead className={denseTable.head}>Signal</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {content.caller_number_stats.results.map(row => (
                                        <TableRow key={row.phone_number ?? row.display}>
                                            <TableCell className={denseTable.cell}>
                                                <CellStack primary={row.display} secondary={row.label && row.phone_number ? row.phone_number : undefined} />
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.attempts)}</TableCell>
                                            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatNumber(row.recorded_calls)}</TableCell>
                                            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>
                                                {formatPercent(row.pickup_rate)}
                                                {row.recorded_calls > 0 && (
                                                    <span className="ml-1 text-xs text-gray-500">
                                                        ({formatNumber(row.picked_up)}/{formatNumber(row.recorded_calls)})
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "text-right tabular-nums")}>{formatDuration(row.average_talk_seconds)}</TableCell>
                                            <TableCell className={denseTable.cell}>{row.underperforming ? <Badge tone="warning">Weaker pickup</Badge> : <EmptyValue />}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Section>

                    <Section
                        icon="Mic"
                        title="Recent recorded calls"
                        actions={
                            <Select value={selectedWrapupKey ?? ALL_WRAPUPS} onValueChange={value => load({ offset: 0, wrapup: value === ALL_WRAPUPS ? null : parseWrapupFilter(value) })}>
                                <SelectTrigger className="h-7 w-56 bg-white" aria-label="Filter recent calls by wrapup">
                                    <SelectValue placeholder="All wrapups" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_WRAPUPS}>All wrapups</SelectItem>
                                    {content.wrapups.map(row => (
                                        <SelectItem key={wrapupFilterValue(wrapupFilter(row))} value={wrapupFilterValue(wrapupFilter(row))}>
                                            {wrapupLabel(row)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        }
                    >
                        <p className="text-xs text-gray-500">{formatNumber(content.recent_calls.count)} matching call reports, newest first.</p>
                        {content.recent_calls.results.length === 0 ? (
                            <StateBlock message="No calls match this filter." />
                        ) : (
                            <Table>
                                <TableHeader className={denseTable.header}>
                                    <TableRow className={denseTable.headerRow}>
                                        <TableHead className={denseTable.head}>Date / time</TableHead>
                                        <TableHead className={denseTable.head}>Caller number</TableHead>
                                        <TableHead className={denseTable.head}>Card</TableHead>
                                        <TableHead className={denseTable.head}>Result</TableHead>
                                        <TableHead className={cn(denseTable.head, "text-right")}>Duration</TableHead>
                                        <TableHead className={denseTable.head}>Callback</TableHead>
                                        <TableHead className={denseTable.head}>Call reference</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {content.recent_calls.results.map(call => (
                                        <TableRow key={call.event_id}>
                                            <TableCell className={cn(denseTable.cell, "whitespace-nowrap tabular-nums")} title={utcTooltip(call.started_at ?? call.recorded_at)}>
                                                {formatParis(call.started_at ?? call.recorded_at)}
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "whitespace-nowrap font-mono text-xs")}>{call.caller_number ?? <EmptyValue />}</TableCell>
                                            <TableCell className={denseTable.cell}>
                                                {call.card.url ? (
                                                    <NavLink className="text-blue-700 hover:text-blue-900 hover:underline" to={call.card.url}>
                                                        {call.card.label}
                                                    </NavLink>
                                                ) : (
                                                    <span className="text-gray-500">{call.card.label}</span>
                                                )}
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "max-w-56")}>
                                                <CellStack primary={call.wrapup} secondary={humanizeToken(call.wrapup_status ?? call.provider_result)} />
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "whitespace-nowrap text-right tabular-nums")}>{formatDuration(call.duration_seconds)}</TableCell>
                                            <TableCell className={denseTable.cell}>
                                                {call.callback_scheduled ? (
                                                    <Badge tone="neutral">
                                                        <CalendarClock className="mr-1 h-3 w-3" /> Scheduled
                                                    </Badge>
                                                ) : (
                                                    <EmptyValue />
                                                )}
                                            </TableCell>
                                            <TableCell className={cn(denseTable.cell, "max-w-48 truncate font-mono text-xs text-gray-500")}>
                                                {call.provider_call_id && call.provider_call_url ? (
                                                    <a
                                                        className="text-blue-700 hover:text-blue-900 hover:underline"
                                                        href={call.provider_call_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title={call.provider_call_id}
                                                    >
                                                        {call.provider_call_id}
                                                    </a>
                                                ) : (
                                                    (call.provider_call_id ?? <EmptyValue />)
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        <div className="flex items-center justify-between">
                            <Button
                                size="sm"
                                variant="outline"
                                className="bg-white"
                                disabled={content.recent_calls.previous_offset === null}
                                onClick={() => load({ offset: content.recent_calls.previous_offset ?? 0 })}
                            >
                                Previous
                            </Button>
                            <span className="text-xs tabular-nums text-gray-500">
                                {formatNumber(content.recent_calls.count === 0 ? 0 : content.recent_calls.offset + 1)}–
                                {formatNumber(Math.min(content.recent_calls.offset + content.recent_calls.results.length, content.recent_calls.count))} of {formatNumber(content.recent_calls.count)}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                className="bg-white"
                                disabled={content.recent_calls.next_offset === null}
                                onClick={() => load({ offset: content.recent_calls.next_offset ?? 0 })}
                            >
                                Next
                            </Button>
                        </div>
                    </Section>
                </div>
            )}
        </div>
    );
}
